import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInWithCustomToken,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, functions, ADMIN_EMAILS } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' or 'salon'
  const [salonData, setSalonData] = useState(null); // Store salon info
  const [loading, setLoading] = useState(true);

  // Login function
  async function login(email, password) {
    // Check if email is in the allowed admin emails list
    if (!ADMIN_EMAILS.includes(email.toLowerCase())) {
      throw new Error('Access denied. Only authorized admin users can login.');
    }
    
    try {
      // Set persistence to local storage before signing in (Firebase built-in mechanism)
      await setPersistence(auth, browserLocalPersistence);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Store/update admin in database
      const adminRef = doc(db, 'admins', user.uid);
      await setDoc(adminRef, {
        email: user.email,
        lastLogin: new Date(),
        role: 'admin',
        isActive: true,
        createdAt: new Date() // This will only be set on first creation due to merge
      }, { merge: true });
      
      return userCredential;
    } catch (error) {
      // Handle specific Firebase auth errors
      if (error.code === 'auth/user-not-found') {
        throw new Error('Admin user not found. Please contact system administrator.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Invalid password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        throw new Error('Invalid email format.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      }
      throw error;
    }
  }

  // Logout function
  function logout() {
    setUserRole(null);
    setSalonData(null);
    return signOut(auth);
  }

  // Salon login with code from mobile app
  async function salonLoginWithCode(salonNumber, loginCode) {
    try {
      // Set persistence to local storage before signing in (Firebase built-in mechanism)
      await setPersistence(auth, browserLocalPersistence);
      
      // Call verifyWebLoginCode Cloud Function (now local, not external)
      const verifyWebLoginCode = httpsCallable(functions, 'verifyWebLoginCode');
      const result = await verifyWebLoginCode({
        salonNumber: salonNumber,
        code: loginCode
      });

      if (!result.data || !result.data.success) {
        throw new Error(result.data?.message || 'Invalid code. Please try again.');
      }

      // If verification is successful, backend returns customToken
      const { customToken } = result.data;

      if (!customToken) {
        throw new Error('Authentication failed. Please try again.');
      }

      // Sign in with custom token
      await signInWithCustomToken(auth, customToken);

      // Fetch salon data using secure Cloud Function
      const getSalonDataFunction = httpsCallable(functions, 'getSalonData');
      const salonDataResult = await getSalonDataFunction();

      if (salonDataResult.data.success) {
        // Set user role and salon data (will be persisted via onAuthStateChanged)
        setUserRole('salon');
        setSalonData(salonDataResult.data.salon);
        console.log('✅ Login complete!');
      } else {
        throw new Error('Failed to fetch salon data.');
      }

      return { success: true };
    } catch (error) {
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  }

  // Fetch salon data for a user
  const fetchSalonData = useCallback(async (uid) => {
    try {
      const getSalonDataFunction = httpsCallable(functions, 'getSalonData');
      const salonDataResult = await getSalonDataFunction();

      if (salonDataResult.data.success) {
        return salonDataResult.data.salon;
      }
      return null;
    } catch (error) {

      return null;
    }
  }, []);

  // Re-fetch salon data for the current user and update context state. AuthContext's salonData
  // is otherwise only populated once at login, so screens that write salon fields (e.g. staff
  // management) must call this after a successful save or other screens reading salonData (e.g.
  // Dashboard) keep seeing the pre-save snapshot until the next login.
  const refreshSalonData = useCallback(async () => {
    if (!currentUser) return null;
    const salon = await fetchSalonData(currentUser.uid);
    setSalonData(salon);
    return salon;
  }, [currentUser, fetchSalonData]);

  // Check if user is admin
  const checkAdminStatus = useCallback(async (user) => {
    if (!user) return false;

    // Check if email is in allowed admin emails
    if (!ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      return false;
    }

    // Check if admin exists in database and is active
    try {
      const adminRef = doc(db, 'admins', user.uid);
      const adminSnap = await getDoc(adminRef);

      if (adminSnap.exists()) {
        const adminData = adminSnap.data();
        return adminData.isActive !== false; // Allow login if isActive is not explicitly false
      }

      // If admin doesn't exist in Firestore but email is in allowed list,
      // create the admin record (this handles the case where you added user in Firebase Auth first)
      await setDoc(adminRef, {
        email: user.email,
        lastLogin: new Date(),
        role: 'admin',
        isActive: true,
        createdAt: new Date()
      });

      return true;
    } catch (error) {

      return false;
    }
  }, []);

  // Determine user role from Firebase auth and Firestore
  const determineUserRole = useCallback(async (user) => {
    if (!user) return null;

    try {
      // Optional: Force token refresh to get latest custom claims
      await user.getIdTokenResult(true);

      // Check if user is admin
      if (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
        const isAdmin = await checkAdminStatus(user);
        if (isAdmin) {
          return 'admin';
        }
      }

      // Check if user is salon owner
      const salonData = await fetchSalonData(user.uid);
      if (salonData) {
        return 'salon';
      }

      // No valid role found
      return null;
    } catch (error) {

      // Don't sign out on error - let the user stay authenticated
      // Return null to indicate role couldn't be determined
      return null;
    }
  }, [checkAdminStatus, fetchSalonData]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);

      if (!user) {
        setCurrentUser(null);
        setUserRole(null);
        setSalonData(null);
        setLoading(false);
        return;
      }

      try {
        // 1. Always set user first (Firebase auth token is valid)
        setCurrentUser(user);

        // 2. Determine role (from Firestore or Cloud Functions)
        const role = await determineUserRole(user);

        if (!role) {
          // ❗ Do NOT sign out immediately on refresh
          // This could be a temporary network issue or slow Cloud Function
          console.warn('Role not resolved yet. User remains authenticated.');
          setLoading(false);
          return;
        }

        setUserRole(role);

        // 3. Load salon data ONLY if salon
        if (role === 'salon') {
          const salon = await fetchSalonData(user.uid);
          setSalonData(salon);
        } else {
          setSalonData(null);
        }

      } catch (error) {

        // Keep user authenticated even on error
        // The route guards will handle redirects if needed
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [determineUserRole, fetchSalonData]);

  const value = {
    currentUser,
    userRole,
    salonData,
    loading,
    login,
    logout,
    salonLoginWithCode,
    refreshSalonData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

